import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import LangSwitch from "@/components/LangSwitch";
import { useLang } from "@/context/LangContext";
import axios from "axios";
import { Eye, EyeSlash, CheckCircle, ArrowLeft, ArrowRight, House } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { t, isRtl } = useLang();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const BackArrow = isRtl ? ArrowRight : ArrowLeft;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError(t('rp_err_pw_len')); return; }
    if (password !== confirm) { setError(t('rp_err_mismatch')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('rp_err_generic'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div dir={isRtl ? "rtl" : "ltr"} className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(165deg, #070709 0%, #0A0D12 50%, #070709 100%)" }}>
        <Link
          to="/"
          data-testid="auth-back-home"
          className={`absolute top-4 ${isRtl ? "right-4" : "left-4"} z-10 inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-slate-300 hover:text-white font-['Outfit'] text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 rounded-lg transition-all`}
        >
          <House size={16} weight="regular" />
          <span className="hidden sm:inline">{t('back_to_home')}</span>
        </Link>
        <div className={`absolute top-4 ${isRtl ? "left-4" : "right-4"} z-10`}><LangSwitch /></div>
        <div className="bg-[#111216] border border-slate-800/60 rounded-2xl p-8 text-center max-w-md">
          <h1 className="font-['Outfit'] text-xl font-bold text-white mb-3">{t('rp_invalid_title')}</h1>
          <p className="text-slate-400 text-sm mb-4">{t('rp_invalid_msg')}</p>
          <Link to="/forgot-password" className="text-[#01AF9B] hover:text-[#01AF9B]/80 font-medium text-sm">{t('rp_invalid_link')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div dir={isRtl ? "rtl" : "ltr"} className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(165deg, #070709 0%, #0A0D12 50%, #070709 100%)" }}>
      <Link
        to="/"
        data-testid="auth-back-home"
        className={`absolute top-4 ${isRtl ? "right-4" : "left-4"} z-10 inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-slate-300 hover:text-white font-['Outfit'] text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 rounded-lg transition-all`}
      >
        <House size={16} weight="regular" />
        <span className="hidden sm:inline">{t('back_to_home')}</span>
      </Link>
      <div className={`absolute top-4 ${isRtl ? "left-4" : "right-4"} z-10`}><LangSwitch /></div>
      <div className="w-full max-w-md animate-fade-in-up" data-testid="reset-password-page">
        <div className="flex items-center gap-3 mb-8 justify-center"><Logo size="xl" /></div>
        <div className="bg-[#111216] border border-slate-800/60 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
          {done ? (
            <div data-testid="reset-success">
              <div className="w-14 h-14 rounded-full bg-[#01AF9B]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={28} className="text-[#01AF9B]" />
              </div>
              <h1 className="font-['Outfit'] text-2xl font-bold text-white mb-2 text-center">{t('rp_done_title')}</h1>
              <p className="text-slate-400 text-sm text-center mb-6">{t('rp_done_msg')}</p>
              <Link to="/login" className="w-full bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2" data-testid="go-login-after-reset">
                {t('rp_signin')}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-['Outfit'] text-2xl font-bold text-white mb-2">{t('rp_title')}</h1>
              <p className="text-slate-400 text-sm mb-6">{t('rp_subtitle')}</p>
              {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-red-400 text-sm" data-testid="reset-error">{error}</div>}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="font-['Outfit'] text-xs font-semibold tracking-wider uppercase text-slate-400 mb-1.5 block">{t('rp_new')}</label>
                  <div className="relative">
                    <input data-testid="reset-password" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required
                      className={`w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none transition-all ${isRtl ? "pl-10" : "pr-10"}`}
                      placeholder={t('rp_pw_ph')} />
                    <button type="button" onClick={() => setShowPw(!showPw)} className={`absolute ${isRtl ? "left-3" : "right-3"} top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300`}>
                      {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="font-['Outfit'] text-xs font-semibold tracking-wider uppercase text-slate-400 mb-1.5 block">{t('rp_confirm')}</label>
                  <input data-testid="reset-confirm" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required
                    className="w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none transition-all"
                    placeholder={t('rp_confirm_ph')} />
                </div>
                <button data-testid="reset-submit" type="submit" disabled={loading}
                  className="w-full bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('rp_submit')}
                </button>
              </form>
              <p className="text-center mt-4">
                <Link to="/login" className="text-slate-500 text-sm hover:text-[#01AF9B] flex items-center justify-center gap-1"><BackArrow size={14} /> {t('rp_back_login')}</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
